import { cosmiconfigSync } from 'cosmiconfig';
import { z } from 'zod';
import { homedir } from 'os';
import { join } from 'path';
import { existsSync, readFileSync, writeFileSync, mkdirSync } from 'fs';
import type { Config } from '../types/index.js';

const configSchema = z.object({
  apiKey: z.string().optional(),
  endpoint: z.string().default('https://api.mutagent.io'),
  format: z.enum(['table', 'json']).default('table'),
  timeout: z.number().default(30000),
  defaultWorkspace: z.string().optional(),
  defaultOrganization: z.string().optional(),
});

const credentialsSchema = z.object({
  apiKey: z.string().optional(),
  endpoint: z.string().optional(),
  defaultWorkspace: z.string().optional(),
  defaultOrganization: z.string().optional(),
  expiresAt: z.string().optional(),
}).loose();

export interface FullCredentials {
  apiKey: string;
  endpoint?: string;
  workspaceId?: string;
  organizationId?: string;
  expiresAt?: string;
}

const CREDENTIALS_DIR = join(homedir(), '.config', 'mutagent');
const CREDENTIALS_FILE = join(CREDENTIALS_DIR, 'credentials.json');

function parseJsonSafe<T>(content: string, schema: z.ZodType<T>): T | null {
  try {
    const parsed: unknown = JSON.parse(content);
    return schema.parse(parsed);
  } catch {
    return null;
  }
}

export function loadConfig(): Config {
  // Load from cosmiconfig (rc files)
  const explorer = cosmiconfigSync('mutagent');
  const result = explorer.search();

  // Safely extract config from cosmiconfig result
  const rcConfig = result?.config as Record<string, unknown> | undefined;

  // Load from credentials file
  let credentials: z.infer<typeof credentialsSchema> | null = null;
  if (existsSync(CREDENTIALS_FILE)) {
    credentials = parseJsonSafe(readFileSync(CREDENTIALS_FILE, 'utf-8'), credentialsSchema);
  }

  // Merge configs: ENV > credentials > rc files > defaults
  const merged = {
    providers: {},
    environment: 'production',
    ...rcConfig,
    ...credentials,
    apiKey: process.env.MUTAGENT_API_KEY ?? credentials?.apiKey ?? (rcConfig?.apiKey as string | undefined),
    endpoint: process.env.MUTAGENT_ENDPOINT ?? credentials?.endpoint ?? (rcConfig?.endpoint as string | undefined),
    defaultWorkspace: credentials?.defaultWorkspace ?? (rcConfig?.defaultWorkspace as string | undefined),
    defaultOrganization: credentials?.defaultOrganization ?? (rcConfig?.defaultOrganization as string | undefined),
  };

  return configSchema.parse(merged) as Config;
}

export function saveCredentials(apiKey: string, endpoint?: string): void {
  if (!existsSync(CREDENTIALS_DIR)) {
    mkdirSync(CREDENTIALS_DIR, { recursive: true });
  }

  const config = loadConfig();
  const credentials = {
    apiKey,
    endpoint: endpoint ?? config.endpoint,
  };

  writeFileSync(CREDENTIALS_FILE, JSON.stringify(credentials, null, 2));
}

/**
 * Save full credentials including workspace and organization info
 * Used by browser auth flow which returns complete credential set
 */
export function saveFullCredentials(creds: FullCredentials): void {
  if (!existsSync(CREDENTIALS_DIR)) {
    mkdirSync(CREDENTIALS_DIR, { recursive: true });
  }

  // Load existing credentials to preserve any custom settings
  let existingCredentials: Record<string, unknown> = {};
  if (existsSync(CREDENTIALS_FILE)) {
    try {
      existingCredentials = JSON.parse(readFileSync(CREDENTIALS_FILE, 'utf-8')) as Record<string, unknown>;
    } catch {
      // Ignore parse errors, start fresh
    }
  }

  const credentials = {
    ...existingCredentials,
    apiKey: creds.apiKey,
    endpoint: creds.endpoint ?? existingCredentials.endpoint ?? 'https://api.mutagent.io',
    defaultWorkspace: creds.workspaceId ?? existingCredentials.defaultWorkspace,
    defaultOrganization: creds.organizationId ?? existingCredentials.defaultOrganization,
    expiresAt: creds.expiresAt,
  };

  writeFileSync(CREDENTIALS_FILE, JSON.stringify(credentials, null, 2));
}

export function clearCredentials(): void {
  if (existsSync(CREDENTIALS_FILE)) {
    writeFileSync(CREDENTIALS_FILE, JSON.stringify({}));
  }
}

export function hasCredentials(): boolean {
  if (process.env.MUTAGENT_API_KEY) return true;
  if (!existsSync(CREDENTIALS_FILE)) return false;

  const credentials = parseJsonSafe(readFileSync(CREDENTIALS_FILE, 'utf-8'), credentialsSchema);
  return credentials?.apiKey !== undefined && credentials.apiKey !== '';
}

export function getApiKey(): string | undefined {
  return process.env.MUTAGENT_API_KEY ?? loadConfig().apiKey;
}

export function setDefaultWorkspace(workspaceId: string): void {
  if (!existsSync(CREDENTIALS_DIR)) {
    mkdirSync(CREDENTIALS_DIR, { recursive: true });
  }

  let existingCredentials: Record<string, unknown> = {};
  if (existsSync(CREDENTIALS_FILE)) {
    try {
      existingCredentials = JSON.parse(readFileSync(CREDENTIALS_FILE, 'utf-8')) as Record<string, unknown>;
    } catch {
      // Ignore parse errors, start fresh
    }
  }

  const updated = {
    ...existingCredentials,
    defaultWorkspace: workspaceId,
  };

  writeFileSync(CREDENTIALS_FILE, JSON.stringify(updated, null, 2));
}

export function setDefaultOrganization(organizationId: string): void {
  if (!existsSync(CREDENTIALS_DIR)) {
    mkdirSync(CREDENTIALS_DIR, { recursive: true });
  }

  let existingCredentials: Record<string, unknown> = {};
  if (existsSync(CREDENTIALS_FILE)) {
    try {
      existingCredentials = JSON.parse(readFileSync(CREDENTIALS_FILE, 'utf-8')) as Record<string, unknown>;
    } catch {
      // Ignore parse errors, start fresh
    }
  }

  const updated = {
    ...existingCredentials,
    defaultOrganization: organizationId,
  };

  writeFileSync(CREDENTIALS_FILE, JSON.stringify(updated, null, 2));
}
